'use client'

import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { TCardFacet, TFacet, TFacetValue } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { FilterValues } from '../../../types/filter-store-v2.types'
import { shouldAddNofollowToFilterLinks } from '../../../utils/filter-utils'
import { buildFilterUrl } from './filter-url-builder'

type Props = {
  onFilterChange: (value: string) => void
  value: string
  attribute: {
    title: string
    seo_alias: string
    items: TFacetValue[]
    is_allow_multiple?: boolean
    is_concat_slug?: boolean
  }
  originalSlug?: string
  currentFilters?: FilterValues
  facets?: TFacet[]
  cardFacets?: TCardFacet[]
}

const ITEMS_LIMIT = 8

export const FilterRadio: React.FC<Props> = ({
  onFilterChange,
  value,
  attribute,
  originalSlug,
  currentFilters = {},
  facets,
  cardFacets,
}) => {
  const [showAll, setShowAll] = useState(false)
  const t = useTranslations('collection_page')

  const shouldShowToggle = attribute.items.length > ITEMS_LIMIT
  const displayItems = showAll
    ? attribute.items
    : attribute.items.slice(0, ITEMS_LIMIT)

  const handleValueChange = (newValue: string) => {
    // RadioGroup không hỗ trợ unselect, nên chỉ set value mới
    onFilterChange(newValue)
  }

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    itemValue: string
  ) => {
    e.preventDefault()
    handleValueChange(itemValue)
  }

  // Check if should add nofollow
  const shouldAddNofollow = shouldAddNofollowToFilterLinks(
    currentFilters,
    facets,
    cardFacets
  )

  // Build URL for each filter option
  const getFilterUrl = (item: TFacetValue): string => {
    if (!originalSlug) return '#'
    return buildFilterUrl({
      originalSlug,
      currentFilters,
      facetKey: attribute.seo_alias,
      facetValue: item.seo_alias,
      isChecked: true, // Radio always selects (no uncheck)
      isMultiple: false, // Radio is single value
      isConcatSlug: attribute.is_concat_slug ?? false,
      facets,
      cardFacets,
    })
  }

  return (
    <div className='flex flex-col gap-1'>
      <RadioGroup value={value} onValueChange={handleValueChange}>
        {displayItems.map((item) => {
          const filterUrl = getFilterUrl(item)
          return (
            <RadioGroupItem key={item.seo_alias} value={item.seo_alias}>
              <a
                href={filterUrl}
                onClick={(e) => handleLinkClick(e, item.seo_alias)}
                className='[display:contents]'
                aria-label={`Filter by ${item.title}`}
                rel={shouldAddNofollow ? 'nofollow' : undefined}
              >
                {item.title}
              </a>
            </RadioGroupItem>
          )
        })}
      </RadioGroup>

      {shouldShowToggle && (
        <Button
          variant='ghost'
          size='small'
          onClick={() => setShowAll(!showAll)}
          className='w-full justify-start text-xs text-neutral-600 hover:text-neutral-900'
        >
          {showAll ? t('button.show_less') : t('button.show_more')}
          {!showAll && ` (${attribute.items.length - ITEMS_LIMIT})`}
        </Button>
      )}
    </div>
  )
}
